/* eslint-disable no-console */
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNotificationsStore } from "stores/notifications";
import { useLocalizationStore } from "stores/localization";
import io, { Socket } from "socket.io-client";
import { STORAGE_AUTH_TOKEN, STORAGE_LANGUAGE_NAME } from "constants/variables";
import { getLocalStorageItem } from "utils/localStorage";
import { setSocketServerAddress } from "utils/socketServerAdress";
import { getSocketConfig } from "utils/getSocketConfig";
import {
  DELIVERABLE_EVENTS,
  getSocketEventHandler,
} from "utils/getSocketEventHandler";

const useWebSocket = (): Socket | null => {
  const token = getLocalStorageItem(STORAGE_AUTH_TOKEN);
  const socketRef = useRef<Socket | null>(null);
  const dispatch = useDispatch();
  const addNotification = useNotificationsStore(
    (state) => state.addNotification,
  );
  const language =
    useLocalizationStore((store) => store.language) ||
    getLocalStorageItem(STORAGE_LANGUAGE_NAME);

  const subscribeToSocketEvents = (socket: Socket) => {
    socket.emit("subscribeForBalanceUpdates", {});
    socket.emit("subscribeForPopupNotifications", {});
    socket.emit("subscribeForReferralUpdates", {});
  };

  const unsubscribeFromSocketEvents = (socket: Socket) => {
    socket.emit("unsubscribeFromBalanceUpdates", {});
    socket.emit("unsubscribeForReferralUpdates", {});
  };

  useEffect(() => {
    if (!token) {
      return;
    }

    const socket = io(
      setSocketServerAddress(),
      getSocketConfig(token, language),
    );
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ Connected to WebSocket");
      subscribeToSocketEvents(socket);
    });
    socket.on("connect_error", (error) =>
      console.error("❌ Connection error:", error),
    );
    socket.on("connect_timeout", (timeout) =>
      console.error("❌ Connection timeout:", timeout),
    );
    socket.on("disconnect", (reason) => {
      console.warn("⚠️ Disconnected:", reason);
      if (token) {
        socket.connect();
      }
    });

    socket.io.on("packet", (packet) => {
      if (packet.type === 2 && Array.isArray(packet.data)) {
        const [eventName, payload] = packet.data;

        if (DELIVERABLE_EVENTS.has(eventName)) {
          const data = `2["eventReceived", ${JSON.stringify({
            eventName,
            receivedAt: Date.now(),
            // eslint-disable-next-line no-underscore-dangle
            __eventId: payload.__eventId,
          })}]`;

          socket.io.engine.transport.send([{ type: "message", data }]);

          console.log(`🔥 [packet] ${eventName}`, payload);
        }

        const handler = getSocketEventHandler(
          eventName,
          dispatch,
          addNotification,
        );

        handler?.(payload);
      }
    });

    return () => {
      unsubscribeFromSocketEvents(socket);
      socket.disconnect();
      socket.removeAllListeners();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit("userLanguageChanged", {
        userLanguage: language.toLowerCase(),
      });
    }
  }, [language]);

  return null;
};

export default useWebSocket;
// /* eslint-disable no-console */
// import { useEffect, useRef } from "react";
// import { useDispatch } from "react-redux";
// import { useNotificationsStore } from "stores/notifications";
// import { useLocalizationStore } from "stores/localization";
// import { Socket } from "engine.io-client";
// import { STORAGE_AUTH_TOKEN, STORAGE_LANGUAGE_NAME } from "constants/variables";
// import { getLocalStorageItem } from "utils/localStorage";
// import { setSocketServerAddress } from "utils/socketServerAdress";
// import {
//   DELIVERABLE_EVENTS,
//   getSocketEventHandler,
// } from "utils/getSocketEventHandler";

// const useWebSocket = () => {
//   const token = getLocalStorageItem(STORAGE_AUTH_TOKEN);
//   const socketRef = useRef<Socket | null>(null);
//   const isConnecting = useRef(false);
//   const dispatch = useDispatch();
//   const addNotification = useNotificationsStore(
//     (state) => state.addNotification,
//   );
//   const language =
//     useLocalizationStore((store) => store.language) ||
//     getLocalStorageItem(STORAGE_LANGUAGE_NAME);

//   const spamMode = localStorage.getItem("spamMode");
//   const spamSystem = localStorage.getItem("spamSystem");
//   const spamBonus = localStorage.getItem("spamBonus");
//   const query = {
//     token,
//     spam: Boolean(spamMode),
//     spamSystem: Boolean(spamSystem),
//     spamBonus: Boolean(spamBonus),
//     userLanguage: language.toLowerCase(),
//   };

//   const subscribeToSocketEvents = (socket: Socket) => {
//     socket.send(JSON.stringify(["subscribeForBalanceUpdates", {}]));
//     socket.send(JSON.stringify(["subscribeForPopupNotifications", {}]));
//     socket.send(JSON.stringify(["subscribeForReferralUpdates", {}]));
//   };

//   const unsubscribeFromSocketEvents = (socket: Socket) => {
//     socket.send(JSON.stringify(["unsubscribeFromBalanceUpdates", {}]));
//     socket.send(JSON.stringify(["unsubscribeForReferralUpdates", {}]));
//   };

//   const createSocket = () => {
//     if (isConnecting.current) {
//       console.log("Already connecting, skipping...");
//       return;
//     }
//     isConnecting.current = true;
//     console.log("Creating new socket instance...");

//     const newSocket = new Socket(setSocketServerAddress(), {
//       transports: ["websocket"],
//       query,
//       withCredentials: false,
//       path: "/socket.io/",
//     });

//     newSocket.on("open", () => {
//       console.log("✅ Connected to Engine.IO");
//       subscribeToSocketEvents(newSocket);
//       isConnecting.current = false;
//     });

//     newSocket.on("error", (error) => {
//       console.error("❌ Connection error:", error);
//       isConnecting.current = false;
//     });

//     newSocket.on("close", (reason) => {
//       console.warn("⚠️ Disconnected:", reason);
//       if (token) {
//         setTimeout(
//           () => {
//             createSocket();
//           },
//           5000 + Math.random() * 5000,
//         );
//       }
//     });

//     newSocket.on("message", (data) => {
//       console.log("📥 Raw message received:", data);
//       try {
//         const cleanData =
//           typeof data === "string" ? data.replace(/^\d+/, "") : data;
//         const packet = JSON.parse(cleanData);
//         if (Array.isArray(packet) && packet.length >= 2) {
//           const [eventName, payload] = packet;
//           if (DELIVERABLE_EVENTS.has(eventName)) {
//             newSocket.send(
//               `2["eventReceived", ${JSON.stringify({
//                 eventName,
//                 receivedAt: Date.now(),
//                 // eslint-disable-next-line no-underscore-dangle
//                 __eventId: payload.__eventId,
//               })}]`,
//             );
//             console.log(`🔥 [packet] ${eventName}`, payload);
//           }
//           const handler = getSocketEventHandler(
//             eventName,
//             dispatch,
//             addNotification,
//           );
//           handler?.(payload);
//         }
//       } catch (e) {
//         console.error("Invalid message:", e, data);
//       }
//     });

//     socketRef.current = newSocket;
//   };

//   useEffect(() => {
//     if (!token) {
//       return;
//     }

//     createSocket();

//     return () => {
//       const socket = socketRef.current;
//       if (socket) {
//         unsubscribeFromSocketEvents(socket);
//         socket.close();
//         socketRef.current = null;
//         isConnecting.current = false;
//       }
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [token]);

//   useEffect(() => {
//     const socket = socketRef.current;
//     if (socket) {
//       socket.send(
//         JSON.stringify([
//           "userLanguageChanged",
//           { userLanguage: language.toLowerCase() },
//         ]),
//       );
//     }
//   }, [language]);

//   return null;
// };

// export default useWebSocket;
